<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import InboxName from 'dashboard/components/widgets/InboxName.vue';
import Spinner from 'shared/components/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, required: true },
  onClose: { type: Function, required: true },
  inbox: { type: Object, required: true },
});

const route = useRoute();
const currentUser = useMapGetter('getCurrentUser');
const loading = ref(false);
const providerConnection = ref({});
let poller = null;

const accountId = computed(() => route.params.accountId);
const inboxId = computed(() => props.inbox.id);
const connection = computed(() => providerConnection.value.connection || 'close');
const qrDataUrl = computed(() => providerConnection.value.qr_data_url);
const error = computed(() => providerConnection.value.error);

const endpointBase = computed(
  () => `/api/v1/accounts/${accountId.value}/inboxes/${inboxId.value}`
);

const requestConfig = computed(() => ({
  headers: {
    api_access_token: currentUser.value?.access_token,
  },
}));

const fetchConnection = async () => {
  try {
    const { data } = await axios.get(
      `${endpointBase.value}/provider_connection`,
      requestConfig.value
    );
    providerConnection.value = data || {};
  } catch (e) {
    providerConnection.value = { connection: 'close' };
  }
};

const setup = async () => {
  loading.value = true;
  try {
    await axios.post(
      `${endpointBase.value}/setup_channel_provider`,
      {},
      requestConfig.value
    );
    await fetchConnection();
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  } finally {
    loading.value = false;
  }
};

const disconnect = async () => {
  loading.value = true;
  try {
    await axios.post(
      `${endpointBase.value}/disconnect_channel_provider`,
      {},
      requestConfig.value
    );
    await fetchConnection();
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  await fetchConnection();
  if (connection.value === 'close' || connection.value === 'reconnecting') {
    await setup();
  }

  poller = setInterval(fetchConnection, 3000);
});

onUnmounted(() => {
  if (poller) clearInterval(poller);
});
</script>

<template>
  <woot-modal :show="show" size="small" @close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('INBOX_MGMT.BAILEYS_LINK_DEVICE.TITLE')"
        :header-content="$t('INBOX_MGMT.BAILEYS_LINK_DEVICE.SUBTITLE')"
      />

      <div class="flex flex-col gap-4 p-8 pt-4">
        <div class="flex flex-col gap-4 items-center">
          <InboxName
            :inbox="inbox"
            class="!text-lg"
            with-phone-number
            with-provider-connection-status
          />

          <template v-if="connection === 'close' || error">
            <p v-if="error" class="text-red-500 text-center">
              {{ error }}
            </p>
            <Button :is-loading="loading" @click="setup">
              {{ $t('INBOX_MGMT.BAILEYS_LINK_DEVICE.GENERATE_QR') }}
            </Button>
          </template>

          <template v-else-if="connection === 'connecting'">
            <div v-if="!qrDataUrl" class="flex flex-col gap-4 items-center">
              <p>{{ $t('INBOX_MGMT.BAILEYS_LINK_DEVICE.GENERATING_QR') }}</p>
              <Spinner />
            </div>
            <img
              v-else
              :src="qrDataUrl"
              alt="QR Code"
              class="w-[276px] h-[276px]"
            />
          </template>

          <template v-else-if="connection === 'reconnecting'">
            <div v-if="!qrDataUrl" class="flex flex-col gap-4 items-center">
              <p>{{ $t('INBOX_MGMT.BAILEYS_LINK_DEVICE.RECONNECTING') }}</p>
              <Spinner />
              <Button :is-loading="loading" @click="setup">
                {{ $t('INBOX_MGMT.BAILEYS_LINK_DEVICE.FORCE_QR') }}
              </Button>
            </div>
            <img
              v-else
              :src="qrDataUrl"
              alt="QR Code"
              class="w-[276px] h-[276px]"
            />
          </template>

          <template v-else-if="connection === 'open'">
            <p class="text-center">{{ $t('INBOX_MGMT.BAILEYS_LINK_DEVICE.CONNECTED') }}</p>
            <div class="flex gap-2">
              <Button ghost :is-loading="loading" @click="disconnect">
                {{ $t('INBOX_MGMT.BAILEYS_LINK_DEVICE.DISCONNECT') }}
              </Button>
              <router-link
                :to="{
                  name: 'inbox_dashboard',
                  params: { inboxId: inbox.id },
                }"
              >
                <Button
                  solid
                  teal
                  :label="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
                />
              </router-link>
            </div>
          </template>
        </div>
      </div>
    </div>
  </woot-modal>
</template>
